import { useSyncExternalStore } from 'react';

type AccessTokenReader = () => string | null;
type AccessTokenSubscriber = (onStoreChange: () => void) => () => void;

let readAccessToken: AccessTokenReader = () => null;
let subscribeAccessToken: AccessTokenSubscriber = () => () => {};

/**
 * Wire the platform token store into api-hooks so auth queries
 * (`useCurrentUserApi`, `useCanUseAppApi`, …) stay disabled until
 * a real access token is present. Apps register once at SDK boot:
 *
 * - Web dashboard: `getAccessToken` + `subscribeToAccessToken` from
 *   `@hero/web-hooks`.
 * - Mobile apps: the same pair from `@hero/hooks` (TanStack cache).
 */
export function registerSessionAccessTokenReader(config: {
  getAccessToken: AccessTokenReader;
  subscribe: AccessTokenSubscriber;
}): void {
  readAccessToken = config.getAccessToken;
  subscribeAccessToken = config.subscribe;
}

/** Non-hook read — for plain functions that need a token check. */
export function getSessionAccessToken(): string | null {
  return readAccessToken();
}

/**
 * Reactive access-token read. `useCurrentUserApi` uses this as the
 * `enabled` gate so `/auth/me` is never fired while logged out.
 */
export function useSessionAccessToken(): string | null {
  return useSyncExternalStore(subscribeAccessToken, readAccessToken, () => null);
}

/** Convenience for composing `enabled` on auth-scoped queries. */
export function useIsSessionAuthenticated(): boolean {
  return useSessionAccessToken() !== null;
}
